/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  PropsWithChildren,
  PropsWithoutRef,
  ReactNode,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import * as React from "react";
import styled, { css, SimpleInterpolation } from "styled-components";
import { devices, flex_centered } from "components/shared/Styles";
import { FloatingPanel, Panel } from "components/shared/FloatingPanel";
import { OverlayTrigger } from "@spectrum-web-components/overlay-trigger";
import { useAnimatedClosure } from "hooks/useAnimatedClosure";
import { useEventListener } from "hooks/useEventListener";

export const CustomModalTrigger: React.FC = ({ children }) => {
  return <>{children}</>;
};

export interface CustomModalRefs {
  close: VoidFunction;
}

type CustomModalProps = {
  maxWidth?: string;
} & (
  | { open: boolean; onClose: VoidFunction }
  | { open?: undefined; onClose?: undefined }
);

// TODO: Split managed and triggered modals into separate components with unambiguous interfaces. It is very confusing
//       to have `shouldBeOpen`/`onClose` in the interface, but be meaningless in triggered mode. Also having the
//       interface enforced at runtime is dangerous.
export const CustomModal: React.ForwardRefExoticComponent<
  PropsWithoutRef<PropsWithChildren<CustomModalProps>> &
    RefAttributes<CustomModalRefs>
> & { Trigger: React.ComponentType } = Object.assign(
  React.forwardRef<CustomModalRefs, CustomModalProps>(
    (
      { children, maxWidth = "577px", open: shouldBeOpen, onClose },
      externalRef
    ) => {
      const overlayRef = useRef<OverlayTrigger | null>(null);
      const triggerRef = useRef<HTMLDivElement | null>(null);
      const contentRef = useRef<HTMLDivElement | null>(null);

      const close = useCallback((): void => {
        (overlayRef.current! as any).open = "none";
        setIsOpen(false);
        onClose?.();
      }, [onClose]);

      const { closing, ref, startClose } =
        useAnimatedClosure<HTMLDivElement>(close);

      useImperativeHandle(externalRef, () => ({ close }));

      let trigger;
      let content: ReactNode[] = [];

      React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;
        if (child.type === CustomModalTrigger) {
          trigger = child;
        } else {
          content.push(child);
        }
      });

      const [isOpen, setIsOpen] = useState(false);

      useEventListener(
        "sp-opened",
        (event) => {
          if (event.target === triggerRef.current) {
            setIsOpen(true);
          }
        },
        () => overlayRef.current
      );

      useEffect((): (() => void) | void => {
        if (isOpen) {
          // A short delay is needed for the content to be rendered and focusable
          const timer = setTimeout(() => {
            contentRef.current?.focus();
          }, 100);
          return () => clearTimeout(timer);
        }
      }, [isOpen]);

      if (
        (shouldBeOpen !== undefined && onClose === undefined) ||
        (onClose !== undefined && shouldBeOpen === undefined)
      ) {
        throw "CustomModal can operate in a managed or unmanaged mode. If you pass either of `open` or `onClose` props you must also pass its pair";
      }

      if (shouldBeOpen !== undefined && trigger !== undefined) {
        throw "CustomModal can operate in a managed or unmanaged mode. You can pass `open` or include a trigger but not both";
      }

      return (
        <overlay-trigger
          ref={overlayRef}
          open={shouldBeOpen ? "click" : undefined}
          type="modal"
        >
          <Backdrop
            slot="click-content"
            open={isOpen && !closing}
            ref={ref}
            data-testid="custom-modal-backdrop"
            {...(isOpen ? {} : { inert: "" })}
          >
            <FeaturesModalPanel
              onClose={(): void => {
                isOpen && startClose();
              }}
              maxWidth={maxWidth}
            >
              <CloseButtonContainer
                onClick={startClose}
                aria-label={I18n.t("dismiss_modal")}
              >
                <sp-icon-close />
              </CloseButtonContainer>
              <Content
                data-testid="modal-content"
                ref={contentRef}
                tabIndex={-1}
              >
                {content}
              </Content>
            </FeaturesModalPanel>
          </Backdrop>
          <div
            slot="trigger"
            ref={triggerRef}
            onClick={(e): void => e.nativeEvent.stopImmediatePropagation()}
          >
            {trigger}
          </div>
        </overlay-trigger>
      );
    }
  ),
  { Trigger: CustomModalTrigger }
);

const Backdrop = styled.div<{ open: boolean }>`
  position: fixed;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  background-color: rgba(84, 88, 237, 0);
  ${flex_centered};

  ${devices.small} {
    padding: 40px 26px;
    box-sizing: border-box;
    align-items: flex-start;
  }

  transition: background-color 0.2s ease-in-out;

  ${Panel} {
    opacity: 0;
    transform: translateY(30px);
    transition:
      transform ease-in-out 0.2s,
      opacity 0.2s;
  }

  ${({ open }): SimpleInterpolation => {
    if (open) {
      return css`
        background-color: rgba(84, 88, 237, 0.8);

        ${Panel} {
          opacity: 100%;
          transform: translateY(0);
        }
      `;
    }
  }}
`;

const FeaturesModalPanel = styled(FloatingPanel)<{ maxWidth: string }>`
  position: relative;
  max-width: min(${({ maxWidth }): string => maxWidth}, 90vw);
  max-height: min(90vh, 100%);
  display: flex;
  box-shadow:
    0px 3px 4px 0px #00000008,
    0px 0px 2px 0px #00000026,
    0px 12px 24px 0px #00000029;
  ${devices.small} {
    max-height: 100%;
    width: 100%;
  }
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  border-radius: 20px;
  width: 40px;
  height: 40px;
  background: white;
  box-shadow: var(--phonos-box-shadow);
  right: 0;
  transform: translateX(14px) translateY(-50%);
  cursor: pointer;
  ${flex_centered}
`;

const Content = styled.div`
  padding: 72px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &:focus {
    outline: none;
  }

  ${devices.small} {
    padding: 32px;
  }
`;
